import * as React from 'react';
import { GripVertical } from 'lucide-react';
import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { cn } from '@/lib/utils';

/**
 * Drag-to-reorder for a flat list.
 *
 * Ported from the venue dashboard's `ui/reorder`, minus the tree half: a
 * currency has no parent, so every row is a legal drop target for every other
 * and the peer-filtering collision detection that file carries has nothing to
 * filter.
 *
 * `@dnd-kit` rather than HTML5 `draggable` for the reason it was chosen there:
 * `dragstart`/`drop` never fire under a finger, so a list reordered that way is
 * decorative on a touchscreen. `@dnd-kit` listens to pointer events — one code
 * path for mouse, pen and touch — and ships a keyboard sensor, so the reorder
 * is reachable without dragging at all.
 *
 * Direction-agnostic: the sort is vertical and the handle is placed by flex
 * order rather than a physical side, so Arabic reads right-to-left with no
 * second code path.
 */

/**
 * The 6px activation distance is what separates a drag from a click. Without
 * it every press on the handle starts a drag, and a row's own buttons spend
 * their lives fighting the gesture.
 */
export function useReorderSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

export interface DragHandleProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Names the row — "drag handle" on its own tells a screen reader nothing. */
  label: string;
  dragging?: boolean;
}

/**
 * A dedicated grip rather than a draggable row: the row already carries an Edit
 * and a Retire button, and a drag that started anywhere would swallow both.
 *
 * `touch-action: none` is the line that makes touch work — without it the
 * browser claims the gesture for scrolling and the pointer stream stops
 * arriving mid-drag. It is scoped to the handle, so the rest of the row still
 * scrolls the page normally.
 */
export const DragHandle = React.forwardRef<HTMLButtonElement, DragHandleProps>(
  ({ label, dragging, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      style={{ touchAction: 'none' }}
      className={cn(
        'flex h-8 w-7 shrink-0 touch-none items-center justify-center rounded-md',
        'text-[var(--muted-foreground)] hover:bg-[var(--surface-hover)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]',
        dragging ? 'cursor-grabbing' : 'cursor-grab',
        'disabled:cursor-not-allowed disabled:opacity-30',
        className,
      )}
      {...props}
    >
      <GripVertical className="h-4 w-4" aria-hidden />
    </button>
  ),
);
DragHandle.displayName = 'DragHandle';
